import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useCookieConsent } from "../context/CookieConsentContext";
import {
  hasPostHogTrackingConfigured,
  hasTawkTrackingConfigured,
} from "../lib/cookieConsent";

function PreferenceToggle({ id, label, description, checked, onChange, disabled }) {
  return (
    <label className="jw-cookie-banner__toggle" htmlFor={id}>
      <input
        id={id}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span className="jw-cookie-banner__toggle-copy">
        <span className="jw-cookie-banner__toggle-label">{label}</span>
        <span className="jw-cookie-banner__toggle-desc">{description}</span>
      </span>
    </label>
  );
}

export default function CookieConsentBanner() {
  const {
    consent,
    showInitialBanner,
    preferencesOpen,
    acceptAll,
    rejectNonEssential,
    savePreferences,
    openPreferences,
    closePreferences,
  } = useCookieConsent();

  const showAnalyticsOption = hasPostHogTrackingConfigured();
  const showSupportOption = hasTawkTrackingConfigured();
  const visible = showInitialBanner || preferencesOpen;

  const [analyticsEnabled, setAnalyticsEnabled] = useState(Boolean(consent?.analytics));
  const [supportEnabled, setSupportEnabled] = useState(Boolean(consent?.support));

  useEffect(() => {
    if (!visible) return;
    setAnalyticsEnabled(Boolean(consent?.analytics));
    setSupportEnabled(Boolean(consent?.support));
  }, [consent, visible]);

  if (!visible) return null;

  const isManaging = preferencesOpen && Boolean(consent);

  return (
    <div
      className="jw-cookie-banner"
      role="region"
      aria-label="Cookie preferences"
      aria-live="polite"
    >
      <div className="jw-cookie-banner__inner">
        <div className="jw-cookie-banner__copy">
          <p className="jw-cookie-banner__title">We use cookies</p>
          <p className="jw-cookie-banner__text">
            We need a few cookies to keep JOBUWIN running securely. With your OK, we&apos;ll also
            use optional cookies for analytics and live chat so we can improve the site and help
            you faster.{" "}
            <Link to="/cookies" className="jw-cookie-banner__link">
              Cookie Policy
            </Link>
          </p>
        </div>

        {preferencesOpen ? (
          <div className="jw-cookie-banner__preferences">
            {showAnalyticsOption ? (
              <PreferenceToggle
                id="cookie-pref-analytics"
                label="Analytics"
                description="PostHog (EU) — helps us see what's working and what feels clunky."
                checked={analyticsEnabled}
                onChange={setAnalyticsEnabled}
              />
            ) : null}
            {showSupportOption ? (
              <PreferenceToggle
                id="cookie-pref-support"
                label="Live chat"
                description="tawk.to — lets you reach us in real time on public pages."
                checked={supportEnabled}
                onChange={setSupportEnabled}
              />
            ) : null}
          </div>
        ) : null}

        <div className="jw-cookie-banner__actions">
          {preferencesOpen ? (
            <>
              <button
                type="button"
                className="jw-public-button jw-public-button--primary"
                onClick={() =>
                  savePreferences({
                    analytics: showAnalyticsOption ? analyticsEnabled : false,
                    support: showSupportOption ? supportEnabled : false,
                  })
                }
              >
                Save preferences
              </button>
              {!isManaging ? (
                <button
                  type="button"
                  className="jw-public-button jw-public-button--outline"
                  onClick={closePreferences}
                >
                  Back
                </button>
              ) : (
                <button
                  type="button"
                  className="jw-public-button jw-public-button--text"
                  onClick={closePreferences}
                >
                  Close
                </button>
              )}
            </>
          ) : (
            <>
              <button
                type="button"
                className="jw-public-button jw-public-button--primary"
                onClick={acceptAll}
              >
                Accept all
              </button>
              <button
                type="button"
                className="jw-public-button jw-public-button--outline"
                onClick={rejectNonEssential}
              >
                Reject non-essential
              </button>
              <button
                type="button"
                className="jw-public-button jw-public-button--text jw-cookie-banner__settings"
                onClick={openPreferences}
              >
                Cookie settings
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
